import React, { useCallback, useRef, useState } from 'react'
import {
  Box,
  Icon,
  Text,
  VStack,
  HStack,
  IconButton,
  useColorModeValue,
  useToken,
  List,
  ListItem
} from '@chakra-ui/react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  CloudArrowUpIcon,
  DocumentIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'

interface FilePickerProps {
  onChange: (files: File[]) => void
  multiple?: boolean
  accept?: string
}

const MotionBox = motion(Box)

const FilePicker: React.FC<FilePickerProps> = ({
  onChange,
  multiple = true,
  accept = '*'
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const [
    blue500,
    blue300,
    gray200,
    gray600,
    blue50,
    blue900,
    gray50,
    gray800,
    gray400
  ] = useToken('colors', [
    'blue.500',
    'blue.300',
    'gray.200',
    'gray.600',
    'blue.50',
    'blue.900',
    'gray.50',
    'gray.800',
    'gray.400'
  ])

  const borderColor = useColorModeValue(gray200, gray600)
  const activeBorderColor = useColorModeValue(blue500, blue300)
  const bgColor = useColorModeValue(gray50, gray800)
  const activeBgColor = useColorModeValue(blue50, blue900)
  const listItemBg = useColorModeValue('white', 'gray.700')

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files) return

      const newFiles = Array.from(files)
      const updatedFiles = multiple ? [...selectedFiles, ...newFiles] : newFiles
      setSelectedFiles(updatedFiles)
      onChange(updatedFiles)
    },
    [multiple, onChange, selectedFiles]
  )

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const onDragLeave = () => {
    setIsDragging(false)
  }

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    handleFiles(e.dataTransfer.files)
  }

  const handleClick = () => {
    inputRef.current?.click()
  }

  const removeFile = (index: number) => {
    const updatedFiles = selectedFiles.filter((_, i) => i !== index)
    setSelectedFiles(updatedFiles)
    onChange(updatedFiles)
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
  }

  return (
    <VStack spacing={4} align="stretch" w="100%">
      <input
        type="file"
        ref={inputRef}
        onChange={(e) => {
          handleFiles(e.target.files)
        }}
        multiple={multiple}
        accept={accept}
        style={{ display: 'none' }}
      />

      <MotionBox
        p={8}
        borderWidth="2px"
        borderStyle="dashed"
        borderRadius="xl"
        borderColor={isDragging ? activeBorderColor : borderColor}
        bg={isDragging ? activeBgColor : bgColor}
        cursor="pointer"
        onClick={handleClick}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        whileHover={{ scale: 1.01, borderColor: activeBorderColor }}
        whileTap={{ scale: 0.99 }}
        transition={{ duration: 0.2 }}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
      >
        <Icon
          as={CloudArrowUpIcon}
          w={10}
          h={10}
          color={isDragging ? blue500 : gray400}
          mb={3}
        />
        <Text fontWeight="semibold" fontSize="lg" mb={1}>
          Click or drag files to upload
        </Text>
        <Text color="gray.500" fontSize="sm">
          Support for multiple files
        </Text>
      </MotionBox>

      <AnimatePresence>
        {selectedFiles.length > 0 && (
          <List spacing={3}>
            {selectedFiles.map((file, index) => (
              <ListItem
                as={motion.li}
                key={`${file.name}-${index}`}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                layout
              >
                <HStack
                  p={3}
                  bg={listItemBg}
                  borderRadius="lg"
                  borderWidth="1px"
                  spacing={4}
                  boxShadow="sm"
                >
                  <Icon as={DocumentIcon} color="blue.400" w={5} h={5} />
                  <VStack align="start" spacing={0} flex={1}>
                    <Text fontWeight="medium" fontSize="sm" noOfLines={1}>
                      {file.name}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {formatFileSize(file.size)}
                    </Text>
                  </VStack>
                  <IconButton
                    aria-label="Remove file"
                    icon={<Icon as={XMarkIcon} />}
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    onClick={(e) => {
                      e.stopPropagation()
                      removeFile(index)
                    }}
                  />
                </HStack>
              </ListItem>
            ))}
          </List>
        )}
      </AnimatePresence>
    </VStack>
  )
}

export default FilePicker
